import { useEffect, useReducer } from 'react';
import styles from './ElapsedTime.module.css';

// A clock that counts up from when the work started.
//
// Its job is to be the one thing on screen that is provably alive. A spinner cannot do that:
// global.css disables every animation under `prefers-reduced-motion`, and even when it spins, a
// spinner spins identically whether the search is working or wedged. A number that changes is
// unambiguous, and it survives the reduced-motion switch because it is text.
//
// `since` is a timestamp that lives on the CHAT MESSAGE, not in component state. React 18 drops
// this component and rebuilds it whenever the page remounts - a background Supabase token
// refresh is enough to do it - and a clock kept in state would silently restart from zero at
// exactly the moment the user is already wondering whether something broke. Reading the origin
// from the message means a remount, a tab switch, or a full reload all resume the true count.
export default function ElapsedTime({ since, className = '' }) {
  const [, tick] = useReducer(n => n + 1, 0);

  useEffect(() => {
    if (!since) return undefined;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [since]);

  if (!since) return null;

  return (
    // aria-hidden because this changes every second: inside a live region it would make a
    // screen reader announce the time over and over instead of the status it belongs to.
    <span className={`${styles.elapsed} ${className}`} aria-hidden="true">
      {formatElapsed(Date.now() - since)}
    </span>
  );
}

// "48s" · "1m 12s" · "3m 04s". Mirrors formatElapsed in backend/services/searchNarrator.js so
// the clock on the banner and the elapsed inside a status line never disagree by a format.
export function formatElapsed(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}
